import { useEffect, useState } from 'react';
import { usePostHog } from '@posthog/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface FilterDescriptionProps {
  ariaId?: string;
  description?: string | null;
  featureFlag?: string | null;
}

function payloadToString(payload: unknown): string | null {
  if (payload == null) return null;
  if (typeof payload === 'string') {
    return payload.length > 0 ? payload : null;
  }
  if (typeof payload === 'number' || typeof payload === 'boolean') {
    return String(payload);
  }
  if (typeof payload === 'object') {
    try {
      const serialised = JSON.stringify(payload);
      return serialised && serialised !== '{}' && serialised !== '[]' ? serialised : null;
    } catch {
      return null;
    }
  }
  return null;
}

export default function FilterDescription({ ariaId, description, featureFlag }: FilterDescriptionProps) {
  const posthog = usePostHog();
  const [payloadText, setPayloadText] = useState<string | null>(() => {
    if (!posthog || !featureFlag) return null;
    return payloadToString(posthog.getFeatureFlagResult(featureFlag)?.payload);
  });

  useEffect(() => {
    if (!posthog || !featureFlag) {
      setPayloadText(null);
      return undefined;
    }
    const resolve = () => setPayloadText(payloadToString(posthog.getFeatureFlagResult(featureFlag)?.payload));
    resolve();
    return posthog.onFeatureFlags(resolve);
  }, [posthog, featureFlag]);

  if (!description && !payloadText) {
    return null;
  }

  return (
    <div
      id={ariaId}
      aria-hidden={ariaId ? 'true' : 'false'}
      className="text-secondary text-xs space-y-1"
    >
      {payloadText ? (
        <div className="filter-feature-flag-payload prose prose-xs max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{payloadText}</ReactMarkdown>
        </div>
      ) : null}
      {description ? <p>{description}</p> : null}
    </div>
  );
}
